<template>
    <div class="report-goals mt-3">
        <div class="row">
            <div class="col-12">
                <h3>Track your Performance Goals
                    <button 
                        v-if="canEditGoal && !isEditing" 
                        class="btn btn-white float-end box-shadowed" 
                        @click.prevent="startEditing"
                    >
                        <img class="icon" src="/img/icons/avid-icons/edit.svg" alt="Edit Icon">  
                        Edit Goals
                    </button>
                </h3>
                <div class="mb-3">
                    <p>
                        Goals help you track and measure your organization's annual goals to 
                        understand what metrics are on target and which need more attention. By setting 
                        goals on the page, the results and comparison will be limited to the information 
                        displayed here. These settings can be set at different visibility levels to share with your team 
                        or keep private.
                    </p>
                </div>

                <div v-if="isEditing || allGoalsAreZero">
                    <report-goals-editor
                        :page="page"
                        :user="user"
                        :client="client"
                        :initial-goal="page.goal"
                        @goal-saved="onGoalSaved"
                        @cancel="isEditing = false"
                    />
                </div>
                <div v-else>
                    <!-- Loading Banner -->
                    <div v-if="isLoadingMetrics" class="alert alert-info d-flex align-items-center mb-4">
                        <i class="fas fa-spinner fa-spin me-2"></i>
                        Loading your goal data...
                    </div>

                    <!-- Metric Summary Cards -->
                    <div 
                        v-if="actualMetrics?.sections?.length > 0" 
                        class="row mb-4 agency-client-table new-card-table"
                    >
                        <div 
                            v-for="metric in metricSummaries" 
                            :key="metric.key" 
                            v-show="metric.goal !== 0"
                            class="col-md-4"
                        >
                            <div class="scorecard-template card p-3 mb-3">
                                <div class="mt-1">
                                    <h1>
                                        <span v-if="metric.key === 'revenue'">
                                            {{ currency_with_zero(metric.actual) }}
                                        </span>
                                        <span v-else>
                                            {{ number_with_zero(metric.actual) }}
                                        </span>
                                        <span 
                                            class="badge ms-2" 
                                            v-if="metric.percentChange != null"
                                            :class="{ 
                                                'bg-green bg-icon-increase': metric.percentChange > 0, 
                                                'bg-red bg-icon-decrease': metric.percentChange < 0 
                                            }"
                                        >
                                            {{ percentage(metric.percentChange/100) }}
                                        </span>
                                    </h1>
                                    <p class="text-muted my-2">
                                        <img 
                                            :src="'/img/icons/avid-icons/' + 
                                                (metric.key === 'revenue' ? 'metric-icon-revenue.svg' : 
                                                 metric.key === 'gifts' ? 'metric-icon-gifts.svg' : 'metric-icon-donor.svg')"
                                            :class="['metric-icon', 'me-1', getMetricIconClass(metric.key)]"
                                            :alt="metric.key + ' icon'"
                                        >
                                        {{ metric.label }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Actual Metrics Charts -->
                    <div v-if="actualMetrics?.sections?.length > 0">
                        <div 
                            v-for="(chart, index) in actualMetrics.sections[0].charts" 
                            :key="chart.id"
                            class="col-12 mb-4 size-container" 
                            :class="chart.size_class" 
                            :id="chart.id" 
                            :data-size="chart.size"
                        >
                            <template v-if="!allGoalsAreZero && hasNonZeroGoalForChart(chart)">
                                <div class="chart-container card position-relative mb-4 flex-grow-1">
                                    <div class="grow">
                                        <chart-template
                                            :chart="chart.chart"
                                            :id="chart.id"
                                            :key="chart.id"
                                            :if_refreshing="false"
                                            :if_on_report_page="true"
                                            :applied_chart_filter="{}"
                                            :if_benchmark_report="false"
                                            :show_title="true"
                                            :allow_download="client != null && chart.chart.type !== 'scorecard' && chart.chart.type !== 'information_card' && chart.chart.type !== 'table' && canExport('insights')"
                                            :allow_deep_dive="false"
                                            :allow_label_toggle="false"
                                            :allow_resize="false"
                                            :current_size="chart.size || 'small'"
                                            :page_filters="actualMetrics?.filters || []"
                                            :page_dimensions="goalApplicableDimensions"
                                            :section_filters="actualMetrics?.sections?.[0]?.filters || []"
                                            :section_id="actualMetrics?.sections?.[0]?.id"
                                            :should_show_summary="false"
                                            :hide_xlarge_side_panel="true"
                                            :show_compressed_numbers="user?.preferences?.show_compressed_numbers !== false"
                                            @downloadChart="downloadChart(chart, index)"
                                        >
                                            <template #title="{ chart: chartData }">
                                                <h3 class="chart-title my-2 ms-3">
                                                    {{ chartData.title.replace('(yoy)', '').trim() }}
                                                </h3>
                                            </template>
                                        </chart-template>
                                    </div>

                                    <!-- Monthly Comparison Table -->
                                    <div class="table-width-responsive mt-4">
                                        <table class="table m-0">
                                            <thead>
                                                <tr class="table-header-row">
                                                    <th>Metric</th>
                                                    <th v-for="month in chart.chart.labels" :key="month">
                                                        {{ month }}
                                                    </th>
                                                    <th>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>{{ maxYearLabel }} Actuals</td>
                                                    <td 
                                                        v-for="(value, index) in chart.chart.data[`${maxYearLabel} Actuals`]" 
                                                        :key="index"
                                                    >
                                                        {{ formatMetricValue(value, chart.chart.metrics[0].format) }}
                                                    </td>
                                                    <td class="fw-bold">
                                                        {{ formatMetricValue(
                                                            sumArray(chart.chart.data[`${maxYearLabel} Actuals`]), 
                                                            chart.chart.metrics[0].format
                                                        ) }}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>{{ maxYearLabel }} Goals</td>
                                                    <td 
                                                        v-for="(value, index) in chart.chart.data[`${maxYearLabel} Goal`]" 
                                                        :key="index"
                                                    >
                                                        {{ formatMetricValue(value, chart.chart.metrics[0].format) }}
                                                    </td>
                                                    <td class="fw-bold">
                                                        {{ formatMetricValue(
                                                            sumArray(chart.chart.data[`${maxYearLabel} Goal`]), 
                                                            chart.chart.metrics[0].format
                                                        ) }}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>% Difference</td>
                                                    <td 
                                                        v-for="(value, index) in chart.chart.data[`${maxYearLabel} Actuals`]" 
                                                        :key="index"
                                                        :class="getPercentClass(
                                                            value, 
                                                            chart.chart.data[`${maxYearLabel} Goal`][index]
                                                        )"
                                                    >
                                                        {{ percentage(calculatePercent(
                                                            value, 
                                                            chart.chart.data[`${maxYearLabel} Goal`][index]
                                                        )) }}
                                                    </td>
                                                    <td 
                                                        class="fw-bold" 
                                                        :class="getPercentClass(
                                                            sumArray(chart.chart.data[`${maxYearLabel} Actuals`]), 
                                                            sumArray(chart.chart.data[`${maxYearLabel} Goal`])
                                                        )"
                                                    >
                                                        {{ percentage(calculatePercent(
                                                            sumArray(chart.chart.data[`${maxYearLabel} Actuals`]), 
                                                            sumArray(chart.chart.data[`${maxYearLabel} Goal`])
                                                        )) }}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import chartExportMixin from '../../mixins/chart_export';
import { Context } from "svgcanvas";
import Swal from 'sweetalert2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import permissionsMixin from '../../mixins/permissions';

export default defineComponent({
    name: 'ReportGoals',
    mixins: [chartExportMixin, permissionsMixin],
    props: {
        page: {
            type: Object,
            required: true
        },
        user: {
            type: Object,
            required: true
        },
        client: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            isSaving: false,
            isEditing: false,
            currentYear: new Date().getFullYear(),
            calendarMonths: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
            metrics: [
                { key: 'revenue', label: 'Revenue ($)' },
                { key: 'donors', label: 'Number of New Donors' },
                { key: 'gifts', label: 'Number of Gifts' }
            ],
            actualMetrics: null,
            isLoadingMetrics: false,
            maxYearLabel: "",
            hasLoadedGoals: false
        };
    },
    computed: {
        timePeriodType() {
            // Extract time period from page dimensions
            if (!this.page.dimensions) return null;
            
            const timePeriodDimension = this.page.dimensions.find(dim => 
                dim.column && dim.column.toLowerCase().includes('time_period.year_type')
            );
            
            return timePeriodDimension?.value || null;
        },
        isCalendarYear() {
            // Use Calendar Year ordering only for "Calendar Year" and "Calendar Year to Date"
            // Use Fiscal Year ordering for "Closed Fiscal Year", "Fiscal Year to Date", and "Trailing 12 Months"
            if (Array.isArray(this.timePeriodType)) {
                return this.timePeriodType.some(period => 
                    period === 'Calendar Year' || period === 'Calendar Year to Date'
                );
            }
            return this.timePeriodType === 'Calendar Year' || this.timePeriodType === 'Calendar Year to Date';
        },
        fiscalYearOrderedMonths() {
            // If Calendar Year is selected, use calendar months (no offset)
            // Otherwise use fiscal year months (with offset)
            const offset = this.isCalendarYear ? 0 : (this.client.fiscal_year_offset || 0);
            return [
                ...this.calendarMonths.slice(offset),
                ...this.calendarMonths.slice(0, offset)
            ];
        },
        canEditGoal() {
            if (!this.page.goal) return true;
            
            if (this.user.is_admin) return true;
            
            if (this.page.goal.visibility_level === 'individual' && this.page.goal.user_id === this.user.id) {
                return this.canEdit('insights');
            }
            if (this.page.goal.visibility_level === 'agency' && this.page.goal.agency_id === this.user.agency_id) {
                return this.canEdit('insights');
            }
            if (this.page.goal.visibility_level === 'all') {
                return this.canEdit('insights');
            }

            return false;
        },
        allGoalsAreZero() {
            if (!this.page.goal?.goal_data?.[this.currentYear]) {
                return !this.hasLoadedGoals;
            }
            
            return this.metrics.every(metric => {
                const yearData = this.page.goal.goal_data[this.currentYear][metric.key] || {};
                return Object.values(yearData).every(value => Number(value) === 0);
            });
        },
        metricSummaries() {
            if (!this.actualMetrics?.sections?.[0]?.charts || !this.page.goal?.goal_data?.[this.currentYear]) {
                return [];
            }

            return this.metrics.map(metric => {
                // Get actual value from the chart data
                const chart = this.actualMetrics.sections[0].charts.find(c => {
                    const metricText = c.chart.metrics[0].text.toLowerCase();
                    return metricText === metric.key || (metricText === 'active donors' && metric.key === 'donors');
                });

                // Calculate goal total for the current year
                const goalTotal = Object.values(this.page.goal.goal_data[this.currentYear][metric.key] || {})
                    .reduce((sum, val) => sum + (Number(val) || 0), 0);
                
                // Sum all values in the current year data array
                const actual = chart ? chart.chart.data[this.maxYearLabel + " Actuals"]?.reduce((sum, val) => sum + (Number(val) || 0), 0) : 0;

                // Calculate percent change
                const percentChange = goalTotal ? ((actual - goalTotal) / goalTotal) * 100 : 0;

                return {
                    ...metric,
                    actual,
                    goal: goalTotal,
                    percentChange
                };
            });
        },
        goalApplicableDimensions() {
            const dimensions = Array.isArray(this.page?.dimensions) ? this.page.dimensions : [];
            return dimensions.filter((dim) => this.dimensionAppliesToGoals(dim));
        }
    },
    watch: {
        'page.goal': {
            handler(newGoal, oldGoal) {
                if (newGoal && (!oldGoal || JSON.stringify(newGoal) !== JSON.stringify(oldGoal))) {
                    this.isEditing = false;
                    this.fetchActualMetrics();
                }
            },
            deep: true
        },
        'page.dimensions': {
            handler() {
                if (this.hasLoadedGoals && this.page.goal) {
                    this.fetchActualMetrics();
                }
            },
            deep: true
        },
        actualMetrics: {
            handler() {
                this.$nextTick(() => {
                    this.initializeTooltips();
                });
            },
            deep: true
        }
    },
    mounted() {
        if (this.page.goal) {
            this.hasLoadedGoals = true;
            this.fetchActualMetrics();
        } else {
            this.startEditing();
        }
        this.$nextTick(() => {
            this.initializeTooltips();
        });
    },
    methods: {
        dimensionAppliesToGoals(dim) {
            const group = dim?.report_page_dimensional_filter_group;
            if (!group || !Array.isArray(group.section_ids) || group.section_ids.length === 0) {
                return true;
            }

            return false;
        },
        buildGoalMetricsPayload() {
            const pageCopy = JSON.parse(JSON.stringify(this.page));
            pageCopy.dimensions = this.goalApplicableDimensions.map((dim) => JSON.parse(JSON.stringify(dim)));

            return pageCopy;
        },
        initializeTooltips() {
            if (window.bootstrap && window.bootstrap.Tooltip) {
                const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
                [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));
            }
        },
        startEditing() {
            this.isEditing = true;
        },
        onGoalSaved(data) {
            this.isEditing = false;
            this.$emit('goal-saved', data);
            
            // Reset the metrics data to trigger a fresh fetch
            this.actualMetrics = null;
            this.isLoadingMetrics = true;

            if (data.goal) {
                this.hasLoadedGoals = true;
            } else {
                this.hasLoadedGoals = false;
            }
            
            // Update the local goal data - the watcher will handle fetching new metrics
            this.page.goal = data.goal;
        },
        processChartWithGoals(chart) {
            if (!this.page.goal?.goal_data || !this.page.goal.goal_data[this.currentYear]) {
                return chart;
            }

            const processedChart = { ...chart };
            const metricKey = chart.metrics[0].text.toLowerCase();
            const goalKey = metricKey === 'active donors' ? 'donors' : metricKey.toLowerCase();
            
            // Set chart title and subtitle
            //Get the keys
            const keys = Object.keys(chart.data);
            processedChart.title = chart.name.replace('(yoy)', '').trim();
            processedChart.subtitle = keys[keys.length - 1] + ' vs Goal';

            // Add goal metric to metrics array
            processedChart.metrics.push({
                text: `${chart.metrics[0].text} Goal`,
                type: 'metric',
                color: 'gray',
                value: chart.metrics[0].value,
                format: chart.metrics[0].format,
                main_category: 'Metrics'
            });

            // Create month mapping (e.g., "Jan" -> "January")
            const monthMap = {
                'Jan': 'January',
                'Feb': 'February',
                'Mar': 'March',
                'Apr': 'April',
                'May': 'May',
                'Jun': 'June',
                'Jul': 'July',
                'Aug': 'August',
                'Sep': 'September',
                'Oct': 'October',
                'Nov': 'November',
                'Dec': 'December'
            };

            // Reverse mapping for creating short month labels
            const reverseMonthMap = Object.fromEntries(
                Object.entries(monthMap).map(([short, long]) => [long, short])
            );

            // Get all months that have goal data
            const goalMonths = this.page.goal.goal_data[this.currentYear]?.[goalKey] || {};
            const monthsWithGoals = Object.keys(goalMonths).filter(month => goalMonths[month] !== 0);
            
            // If there are months with goals, expand labels and data to include all goal months
            if (monthsWithGoals.length > chart.labels.length) {
                // Create full labels array in fiscal year order (respecting fiscal_year_offset)
                const fullLabels = this.fiscalYearOrderedMonths
                    .filter(month => monthsWithGoals.includes(month))
                    .map(month => reverseMonthMap[month]);
                
                // Create a map of existing chart labels to their data values
                const existingDataMap = {};
                chart.labels.forEach((label, index) => {
                    if (processedChart.data[this.maxYearLabel]) {
                        existingDataMap[label] = processedChart.data[this.maxYearLabel][index];
                    }
                });
                
                // Build full actual data array with zeros for missing months
                const fullActualData = fullLabels.map(label => existingDataMap[label] || 0);
                
                // Build full goal data array
                const fullGoalData = fullLabels.map(shortMonth => {
                    const fullMonth = monthMap[shortMonth];
                    return goalMonths[fullMonth] || 0;
                });
                
                // Update chart with full labels and data
                processedChart.labels = fullLabels;
                processedChart.data = {
                    [`${this.maxYearLabel} Goal`]: fullGoalData,
                    [`${this.maxYearLabel} Actuals`]: fullActualData
                };
            } else {
                // Original logic for when labels already match or exceed goal months
                const goalData = {};
                goalData[this.maxYearLabel] = chart.labels.map(shortMonth => {
                    const fullMonth = monthMap[shortMonth];
                    return this.page.goal.goal_data[this.currentYear]?.[goalKey]?.[fullMonth] || 0;
                });

                if(processedChart.data[this.maxYearLabel] != undefined) {
                    processedChart.data = {
                        [`${this.maxYearLabel} Goal`]: goalData[this.maxYearLabel],
                        [`${this.maxYearLabel} Actuals`]: processedChart.data[this.maxYearLabel] || []
                    };
                } else {
                    const all_zeros = Array(goalData[this.maxYearLabel].length).fill(0);
                    processedChart.data = {
                        [`${this.maxYearLabel} Goal`]: goalData[this.maxYearLabel],
                        [`${this.maxYearLabel} Actuals`]: all_zeros
                    };
                }
            }
            
            return processedChart;
        },
        fetchActualMetrics() {
            this.isLoadingMetrics = true;
            const payload = this.buildGoalMetricsPayload();
            window.axios.post('/api/insights/' + this.client.url + '/goals/actual-metrics', payload)
                .then(response => {

                    this.maxYearLabel = "";

                    //Determine what the max year label is
                    response.data.goal_page.sections[0].charts.forEach(chart => {
                        //Based off the key of the chart data, find the max year
                        const keys = Object.keys(chart.chart.data);
                        //It isn't a number, it is a string so do a string comparison
                        const maxYear = keys[keys.length - 1];
                        //If it is greater than the current max, set it
                        if (maxYear > this.maxYearLabel) {
                            this.maxYearLabel = maxYear;
                        }
                    });

                    // Process each chart to add goals
                    if (response.data.goal_page.sections && response.data.goal_page.sections.length > 0) {
                        response.data.goal_page.sections[0].charts = response.data.goal_page.sections[0].charts.map(chartData => ({
                            ...chartData,
                            chart: this.processChartWithGoals(chartData.chart)
                        }));
                    }
                    this.actualMetrics = response.data.goal_page;
                    this.hasLoadedGoals = true;
                })
                .catch(error => {
                    Swal.fire({
                        title: 'Error',
                        text: error.response?.data?.message || 'There was an error fetching actual metrics. Please try again.',
                        icon: 'error',
                        confirmButtonText: 'OK',
                        customClass: {
                            popup: 'swal-error'
                        }
                    });
                })
                .finally(() => {
                    this.isLoadingMetrics = false;
                });
        },
        downloadChart(chart_div, i) {
            //Add the necessary modifications to canvas2svg library to work with chartjs
            Context.prototype.getContext = function (contextId) {
                if (contextId=="2d" || contextId=="2D")
                    return this;
                return null;
            };
            Context.prototype.style = function() { return this.__canvas.style; };
            Context.prototype.getAttribute = function(name) { return this[name]; };
            Context.prototype.addEventListener = function(type, listener, eventListenerOptions) {};

            this.loading = true;
            window.axios.get('/api/export/sizes/' + this.client.id)
                .then(response => {
                    const sizes = response.data.sizes;
                    
                    const container = document.getElementById(chart_div.id);
                    const originalChart = container.querySelector('canvas');
                    const chartjs = Chart.getChart(originalChart);

                    // Create a temporary container and canvas for export
                    const tempContainer = document.createElement('div');
                    tempContainer.style.position = 'absolute';
                    tempContainer.style.left = '-9999px';
                    tempContainer.style.top = '-9999px';
                    document.body.appendChild(tempContainer);

                    const tempCanvas = document.createElement('canvas');
                    tempContainer.appendChild(tempCanvas);

                    const size = container.dataset.size;
                    
                    if (size === 'x-large' || size === 'large') {
                        tempCanvas.width = sizes.FULL_WIDTH.width;
                        tempCanvas.height = sizes.FULL_WIDTH.height;
                    } else if (size === 'small') {
                        tempCanvas.width = sizes.SINGLE_SMALL.width;
                        tempCanvas.height = sizes.SINGLE_SMALL.height;
                    } else {
                        tempCanvas.width = sizes.FULL_WIDTH.width;
                        tempCanvas.height = sizes.FULL_WIDTH.height;
                    }

                    // Get visible chart data (filtering out hidden datasets)
                    const filteredData = this.getVisibleChartData(chartjs);
                    
                    // Get the original datalabels config and wrap formatter to use original indices
                    const originalDatalabels = chartjs.config.options.plugins?.datalabels || {};
                    const originalFormatter = originalDatalabels.formatter;
                    
                    // Create a formatter that honors the original chart's label visibility
                    const exportFormatter = originalFormatter 
                        ? function(value, context) {
                            const dataset = context.dataset;
                            const originalIndex = dataset.__originalIndex;
                            
                            if (originalIndex !== undefined) {
                                try {
                                    const originalContext = { ...context, datasetIndex: originalIndex };
                                    return originalFormatter.call(this, value, originalContext);
                                } catch (e) {
                                    return null;
                                }
                            }
                            
                            try {
                                return originalFormatter.call(this, value, context);
                            } catch (e) {
                                return null;
                            }
                        }
                        : undefined;
                    
                    // Clone the chart configuration with specific options for export
                    const tempChart = new Chart(tempCanvas, {
                        type: chartjs.config.type,
                        data: filteredData,
                        plugins: [ChartDataLabels],
                        options: {
                            ...JSON.parse(JSON.stringify(chartjs.config.options)),
                            responsive: false,
                            maintainAspectRatio: false,
                            animation: false,
                            layout: {
                                padding: chartjs.config.options.layout?.padding || 0
                            },
                            scales: {
                                ...chartjs.config.options.scales,
                                x: {
                                    ...chartjs.config.options.scales.x,
                                    ticks: {
                                        ...chartjs.config.options.scales.x.ticks,
                                        font: chartjs.config.options.scales.x.ticks.font
                                    }
                                },
                                y: {
                                    ...chartjs.config.options.scales.y,
                                    ticks: {
                                        ...chartjs.config.options.scales.y.ticks,
                                        font: chartjs.config.options.scales.y.ticks.font,
                                        callback: chartjs.config.options.scales.y.ticks.callback
                                    }
                                }
                            },
                            plugins: {
                                ...chartjs.config.options.plugins,
                                legend: {
                                    ...chartjs.config.options.plugins.legend,
                                    labels: {
                                        ...chartjs.config.options.plugins.legend.labels,
                                        font: chartjs.config.options.plugins.legend.labels.font
                                    }
                                },
                                datalabels: {
                                    ...originalDatalabels,
                                    font: originalDatalabels.font,
                                    ...(exportFormatter ? { formatter: exportFormatter } : {})
                                }
                            }
                        }
                    });

                    // Force multiple renders to ensure stability
                    tempChart.update('none');
                    tempChart.update('none');

                    // Wait for the chart to be fully rendered
                    setTimeout(() => {
                        let svg = this.convertToSVG(tempCanvas);
                        svg = this.updateTextElements(svg);
                        
                        if (container.querySelector('.line-chart-template') || container.querySelector('.bar-line-chart-template')) {
                            svg = this.addLinesToChart(svg);
                        }

                        // Use filtered data (without hidden datasets) for export
                        const chart_data = filteredData;
                        
                        // Ensure we have all necessary chart data
                        const chartInfo = {
                            id: chart_div.id,
                            title: chart_div.chart.title,
                            subtitle: this.maxYearLabel + " " + chart_div.chart.metrics?.[0]?.text + ' vs Goal',
                            svg: svg,
                            data: chart_data,
                            size: size,
                            type: 'line',
                            metrics: chart_data.datasets.map(dataset => ({
                                text: dataset.label,
                                type: 'metric',
                                color: dataset.borderColor,
                                format: chart_div.chart.metrics?.[0]?.format || 'number',
                                main_category: 'Metrics'
                            })),
                            labels: chart_data.labels
                        };

                        const data = {
                            client: this.client,
                            page: this.page,
                            params: document.location.search !== '' ? document.location.search.substring(1) : '',
                            is_goal_section: true,
                            svg: chartInfo
                        };

                        window.axios.post('/api/export/chart', data, { responseType: 'blob' })
                            .then(response => {
                                // Create a URL for the blob and trigger a download
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = url;

                                const name = `${chart_div.chart.title} - Goal Chart #${chart_div.id} - ${this.getDate()}.pptx`;

                                link.setAttribute('download', name);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                this.loading = false;

                                // Clean up temporary elements
                                tempChart.destroy();
                                tempContainer.remove();
                            })
                            .catch(error => {
                                // Clean up temporary elements
                                tempChart.destroy();
                                tempContainer.remove();

                                Swal.fire({
                                    title: 'Error',
                                    text: 'Failed to export chart. Please try again.',
                                    icon: 'error',
                                    confirmButtonText: 'OK',
                                    customClass: {
                                        popup: 'swal-error'
                                    }
                                });
                                this.loading = false;
                            });
                    }, 100);
                })
                .catch(error => {
                    Swal.fire({
                        title: 'Error',
                        text: 'Failed to produce the slides. Please try again.',
                        icon: 'error',
                        confirmButtonText: 'OK',
                        customClass: {
                            popup: 'swal-error'
                        }
                    });
                    this.loading = false;
                });
        },
        hasNonZeroGoalForChart(chart) {
            if (!this.page.goal?.goal_data?.[this.currentYear]) return false;
            
            const metricKey = chart.chart.metrics[0].text.toLowerCase();
            const goalKey = metricKey === 'active donors' ? 'donors' : metricKey.toLowerCase();
            
            const yearData = this.page.goal.goal_data[this.currentYear][goalKey] || {};
            return Object.values(yearData).some(value => Number(value) !== 0);
        },
        formatMetricValue(value, format) {
            if (format === 'money') {
                return this.currency_with_zero(value);
            }
            return this.number_with_zero(value);
        },
        calculatePercent(actual, goal) {
            if (!goal) return 0;
            return ((actual - goal) / goal);
        },
        getPercentClass(actual, goal) {
            if (!goal) return '';
            const percent = ((actual - goal) / goal);
            return {
                'text-success': percent > 0,
                'text-danger': percent < 0
            };
        },
        sumArray(array) {
            return array.reduce((sum, value) => sum + (Number(value) || 0), 0);
        },
        getMetricIconClass(metricKey) {
            const iconClassMap = {
                'revenue': 'icon-green',
                'gifts': 'icon-yellow',
                'donors': 'icon-blue'
            };
            return iconClassMap[metricKey] || '';
        }
    }
});
</script>